import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Modal, StatusChip, Text } from '@fragments';
import { IconAlertTriangle, IconArrowRight, IconBrandMeta, IconCheck, IconChevronLeft, IconLockCheck, IconMessageCircle, IconMinus, IconPlugConnected, IconRefresh, IconSend, IconChartBar, IconExchange, IconUnlink } from '@tabler/icons-react';
import { PLATFORM_ICONS, PLATFORM_LABELS } from '../../../../enums/platforms';
import { STUDIO_AUTOMATIONS } from '../../../../enums/routes';
import { formatNumber } from '../../utils/format';
import SocialAvatar from '../../../../components/SocialAvatar';
import ConnectScene from '../../components/ConnectScene';
import PageState from '../../components/PageState';
import useConnect from './hooks/useConnect';
import './styles.scss';

const CAPABILITY_ICONS = { comments: IconMessageCircle, messages: IconSend, insights: IconChartBar };

// One capability of the connection: what it lets the Studio do, whether granted or not.
function CapabilityRow({ name, granted }) {
  const { t } = useTranslation('studioAutomations');
  const Glyph = CAPABILITY_ICONS[name];
  return <li className={`studio-connect__capability${granted === false ? ' is-missing' : ''}`}>
    <span className="studio-connect__capability-icon" aria-hidden="true"><Glyph /></span>
    <span className="studio-connect__capability-text">
      <Text.label contrast={9}>{t(`capability_${name}`)}</Text.label>
      <Text.caption contrast={6}>{t(`capability_${name}_hint`)}</Text.caption>
    </span>
    {granted !== undefined && <span className="studio-connect__capability-state" aria-label={t(granted ? 'capabilityGranted' : 'capabilityMissing')}>{granted ? <IconCheck /> : <IconMinus />}</span>}
  </li>;
}

function Channels({ connect }) {
  const { t } = useTranslation('studioAutomations');
  return <div className="studio-connect__stack">
    <Text.label contrast={8}>{t('connectChannels')}</Text.label>
    <ul className="studio-connect__channels">
      {connect.platforms.map((option, index) => {
        const Glyph = PLATFORM_ICONS[option.platform];
        const available = option.status === 'available';
        return <li key={option.platform} className="studio-automations__rise" style={{ '--rise-index': index }}>
          <button type="button" className="studio-connect__channel" disabled={!available} onClick={() => connect.chooseChannel(option.platform)}>
            <span className="studio-connect__channel-icon" aria-hidden="true"><Glyph /></span>
            <span className="studio-connect__channel-text">
              <Text.label contrast={available ? 9 : 6}>{PLATFORM_LABELS[option.platform]}</Text.label>
              <Text.caption contrast={6}>{t(`channelHint_${option.platform}`)}</Text.caption>
            </span>
            {available ? <IconArrowRight className="studio-connect__channel-go" /> : <StatusChip.Default text={t('studio:soon')} preserveCase />}
          </button>
        </li>;
      })}
    </ul>
  </div>;
}

function InstagramStep({ connect }) {
  const { t } = useTranslation('studioAutomations');
  const code = connect.outcome?.code;
  const errorText = code && t(`connectError_${code}`, { defaultValue: t('connectError_generic') });
  return <div className="studio-connect__stack">
    <div className="studio-connect__heading">
      <Text.h5 weight={500} contrast={9}>{t('connectStepTitle')}</Text.h5>
      <Text.p contrast={7}>{t('connectStepText')}</Text.p>
    </div>
    {errorText && <div className="studio-connect__error" role="alert"><IconAlertTriangle /><Text.span contrast={8}>{errorText}</Text.span></div>}
    <ul className="studio-connect__capabilities">{Object.keys(CAPABILITY_ICONS).map(name => <CapabilityRow key={name} name={name} />)}</ul>
    <Button variant="rounded" color="inverse" size="large" fullWidth startIcon={<IconBrandMeta />} loading={connect.busy === 'connect'} preventLoading onClick={() => connect.connect()}>{t('connectMeta')}</Button>
    <div className="studio-connect__assurance">
      <IconLockCheck />
      <Text.caption contrast={6}>{t('connectAssurance')}</Text.caption>
    </div>
    <Button variant="text" color="overlay" size="small" startIcon={<IconChevronLeft />} preventLoading onClick={connect.backToChannels}>{t('connectOtherChannel')}</Button>
  </div>;
}

function ConnectedAccount({ studio: s, connect }) {
  const { t } = useTranslation('studioAutomations');
  const { connection } = connect;
  const { account } = connection;
  const missing = connection.missing_capabilities;
  const figures = [account.followers_count !== null && t('connectedFollowers', { count: formatNumber(account.followers_count, s.locale) }), account.media_count !== null && t('connectedMedia', { count: formatNumber(account.media_count, s.locale) })].filter(Boolean);
  return <div className="studio-connect__stack">
    <div className="studio-connect__account">
      <SocialAvatar className="studio-connect__avatar" picture={account.picture} label={account.handle} platform={connection.platform} />
      <div className="studio-connect__account-text">
        <Text.label contrast={9} maxLines={1}>{`@${account.handle}`}</Text.label>
        <Text.caption contrast={6} maxLines={1}>{[t(`accountType_${account.account_type}`), ...figures].join(' · ')}</Text.caption>
      </div>
      {!missing.length ? <StatusChip.Success text={t('connectedChip')} preserveCase /> : <StatusChip.Alert text={t('connectedPartialChip')} preserveCase />}
    </div>
    {missing.length > 0 && <div className="studio-connect__error" role="status"><IconAlertTriangle /><Text.span contrast={8}>{t(missing.includes('messages') ? 'connectedMissingMessages' : 'connectedMissing')}</Text.span></div>}
    <ul className="studio-connect__capabilities">{Object.keys(CAPABILITY_ICONS).map(name => <CapabilityRow key={name} name={name} granted={connection.capabilities.includes(name)} />)}</ul>
    {missing.length > 0
      ? <Button variant="rounded" color="inverse" size="large" fullWidth loading={connect.busy === 'connect'} preventLoading onClick={() => connect.connect({ reauth: true })}>{t('connectGrant')}</Button>
      : <Button variant="rounded" color="inverse" size="large" fullWidth endIcon={<IconArrowRight />} preventLoading onClick={() => s.navigate(STUDIO_AUTOMATIONS)}>{t('connectContinue')}</Button>}
    <div className="studio-connect__row">
      <Button variant="rounded" color="overlay" startIcon={<IconExchange />} loading={connect.busy === 'connect' && !missing.length} preventLoading onClick={() => connect.connect({ reauth: true })}>{t('connectChangeAccount')}</Button>
      <Button variant="rounded" discreet color="error" startIcon={<IconUnlink />} loading={connect.busy === 'disconnect'} preventLoading onClick={() => window.openConfirm({
        title: t('disconnectTitle'), headline: t('disconnectHeadline', { handle: account.handle }), subtitle: t('disconnectText'),
        buttons: [{ text: t('studio:cancel'), color: 'overlay', onClick: () => {} }, { text: t('disconnectConfirm'), color: 'error', onClick: connect.disconnect }],
      })}>{t('disconnect')}</Button>
    </div>
  </div>;
}

function Reauthorize({ connect }) {
  const { t } = useTranslation('studioAutomations');
  const { connection } = connect;
  return <div className="studio-connect__stack">
    <div className="studio-connect__account">
      <SocialAvatar className="studio-connect__avatar" picture={connection.account?.picture} label={connection.account?.handle} platform={connection.platform} />
      <div className="studio-connect__account-text">
        <Text.label contrast={9} maxLines={1}>{`@${connection.account?.handle}`}</Text.label>
        <Text.caption contrast={6}>{t('reauthorizeCaption')}</Text.caption>
      </div>
      <StatusChip.Alert text={t('reauthorizeChip')} preserveCase />
    </div>
    <div className="studio-connect__heading">
      <Text.h5 weight={500} contrast={9}>{t('reauthorizeTitle')}</Text.h5>
      <Text.p contrast={7}>{t('reauthorizeText')}</Text.p>
    </div>
    <Button variant="rounded" color="inverse" size="large" fullWidth startIcon={<IconBrandMeta />} loading={connect.busy === 'connect'} preventLoading onClick={() => connect.connect({ reauth: true })}>{t('reauthorizeAction')}</Button>
  </div>;
}

// `account_in_use` / `account_mismatch`: what was authorized, never whose it is unless it is the creator's.
function Conflict({ studio: s, connect }) {
  const { t } = useTranslation('studioAutomations');
  const { conflict } = connect.connection;
  const own = conflict.own_profile_id ? s.accounts.list.find(item => item.id === conflict.own_profile_id) : null;
  const key = conflict.code === 'account_mismatch' ? 'Mismatch' : own ? 'Own' : 'InUse';
  return <div className="studio-connect__stack">
    <span className="studio-connect__conflict-mark" aria-hidden="true"><IconAlertTriangle /></span>
    <div className="studio-connect__heading">
      <Text.h5 weight={500} contrast={9}>{t(`conflict${key}Title`, { handle: conflict.account.handle })}</Text.h5>
      <Text.p contrast={7}>{t(`conflict${key}Text`, { handle: conflict.account.handle, profile: s.profile.handle })}</Text.p>
    </div>
    <div className="studio-connect__row">
      <Button variant="rounded" color="inverse" startIcon={<IconExchange />} loading={connect.busy === 'connect'} preventLoading onClick={() => connect.connect({ reauth: true })}>{t('connectChangeAccount')}</Button>
      {own && <Button variant="rounded" color="overlay" preventLoading onClick={() => s.accounts.select(own.id)}>{t('conflictOpenProfile', { handle: own.handle })}</Button>}
    </div>
  </div>;
}

// `?mock=true` only: the Meta dialog's outcomes, as the mock replaces Stripe with a test form.
function MetaStandIn({ connect }) {
  const { t } = useTranslation('studioAutomations');
  const answers = [['connected', 'metaAllow', 'inverse'], ['missing_messages', 'metaNoMessages', 'overlay'], ['account_mismatch', 'metaOtherAccount', 'overlay'], ['account_in_use', 'metaInUse', 'overlay'], ['not_professional', 'metaPersonal', 'overlay'], ['cancelled', 'metaCancel', 'overlay']];
  return <Modal open size="sm" title={t('metaTitle')} subtitle={t('metaSubtitle')} closeLabel={t('studio:close')} onClose={connect.cancelMeta}>
    <div className="studio-connect__meta">
      {answers.map(([value, label, color]) => <Button key={value} fullWidth variant="rounded" color={color} loading={connect.busy === value} disabled={!!connect.busy} preventLoading onClick={() => connect.answerMeta(value)}>{t(label)}</Button>)}
    </div>
  </Modal>;
}

// /studio/automations/connect — where the creator picks a channel and authorizes the account. The
// illustration on the left stays; the right side follows the connection's state.
export default function Connect({ studio: s }) {
  const { t } = useTranslation('studioAutomations');
  const connect = useConnect(s);
  const title = connect.view === 'channels' ? t('connectChannelsTitle') : t('connectInstagramTitle');
  const subtitle = connect.view === 'channels' ? t('connectChannelsText') : t('connectInstagramText');
  const missing = connect.connection?.status === 'connected' ? connect.connection.missing_capabilities : [];
  return <div className="studio-connect">
    <div className="studio-connect__art">
      <div className="studio-connect__scene" role="img" aria-label={t('sceneLabel')}><ConnectScene key={missing.join()} handle={connect.connection?.account?.handle || s.profile.handle} picture={s.profile.picture} missing={missing} /></div>
      <div className="studio-connect__pitch">
        <Text.h2 weight={400} contrast={9}>{title}</Text.h2>
        <Text.p contrast={7}>{subtitle}</Text.p>
        <Button variant="text" color="overlay" size="small" startIcon={<IconChevronLeft />} preventLoading onClick={() => s.navigate(STUDIO_AUTOMATIONS)}>{t('connectBack')}</Button>
      </div>
    </div>
    <Card flat className="studio-connect__panel">
      {connect.status === 'loading' ? <div className="studio-connect__stack" aria-busy="true"><Text.label loading>{t('connectChannels')}</Text.label><Text.p loading>{t('connectChannelsText')}</Text.p><Text.p loading>{t('connectStepText')}</Text.p></div>
        : connect.status === 'error' ? <PageState compact icon={<IconPlugConnected />} text={t('loadError')} action={<Button variant="rounded" color="overlay" startIcon={<IconRefresh />} preventLoading onClick={connect.reload}>{t('studio:retry')}</Button>} />
        : connect.view === 'channels' ? <Channels connect={connect} />
        : connect.view === 'instagram' ? <InstagramStep connect={connect} />
        : connect.view === 'connected' ? <ConnectedAccount studio={s} connect={connect} />
        : connect.view === 'reauthorize' ? <Reauthorize connect={connect} />
        : <Conflict studio={s} connect={connect} />}
    </Card>
    {connect.authorizing && <MetaStandIn connect={connect} />}
  </div>;
}
